import type {
  MetricCompleteness as ContractMetricCompleteness,
  MetricResultEnvelope as ContractMetricResultEnvelope,
  MetricScope as ContractMetricScope,
  MetricUnit as ContractMetricUnit,
  MetricWindow as ContractMetricWindow,
} from '@signbento/contracts'

/** One public metric shape, owned by contracts (P17-07). */

export const METRIC_VERSION = 1

export type MetricScope = ContractMetricScope
export type MetricUnit = ContractMetricUnit
export type MetricCompleteness = ContractMetricCompleteness
export type MetricWindow = ContractMetricWindow

export interface MetricContext {
  readonly as_of: string
  readonly timezone: string
  readonly window?: MetricWindow
}

export interface MetricResponse<T> extends Omit<ContractMetricResultEnvelope, 'value'> {
  readonly value: T | null
}

export interface MetricSpec<T> {
  readonly metric_id: string
  readonly definition_version?: number
  readonly value: T | null
  readonly unit: MetricUnit
  readonly scope: MetricScope
  readonly population: string
  readonly context: MetricContext
  readonly sample_size?: number | null
  readonly excluded_count?: number | null
  readonly unknown_count?: number | null
  readonly completeness?: MetricCompleteness
  readonly retained_from?: string | null
  readonly retained_to?: string | null
  readonly stale?: boolean
  readonly provisional?: boolean
  readonly drilldown_filter?: Readonly<Record<string, string | number | boolean>>
  readonly numerator?: number | null
  readonly denominator?: number | null
  readonly notes?: readonly string[]
}

export function metric<T>(spec: MetricSpec<T>): MetricResponse<T> {
  return {
    metric_id: spec.metric_id,
    definition_version: spec.definition_version ?? METRIC_VERSION,
    value: spec.value,
    unit: spec.unit,
    scope: spec.scope,
    as_of: spec.context.as_of,
    window: spec.context.window ?? null,
    timezone: spec.context.timezone,
    population: spec.population,
    sample_size: spec.sample_size ?? null,
    excluded_count: spec.excluded_count ?? null,
    unknown_count: spec.unknown_count ?? null,
    completeness: spec.completeness ?? 'complete',
    retained_bounds: { from: spec.retained_from ?? null, to: spec.retained_to ?? null },
    stale: spec.stale ?? false,
    provisional: spec.provisional ?? false,
    drilldown_scope: { authorized: true, filters: spec.drilldown_filter ?? {} },
    notes: [...(spec.notes ?? [])],
    ...(spec.numerator === undefined ? {} : { numerator: spec.numerator }),
    ...(spec.denominator === undefined ? {} : { denominator: spec.denominator }),
  }
}

/** Documented rounding: one decimal, half rounds toward +∞ (rates are non-negative). */
export function round1(value: number): number {
  return Math.round(value * 10) / 10
}

export function percent1(numerator: number, denominator: number): number | null {
  if (denominator === 0) return null
  return round1((100 * numerator) / denominator)
}

/** Middle value, or the mean of the two middle values for even n. Null for empty. */
export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) {
    return sorted[middle] ?? null
  }
  const lower = sorted[middle - 1]
  const upper = sorted[middle]
  if (lower === undefined || upper === undefined) return null
  return (lower + upper) / 2
}
